/** Eight certificate-depth examples per part for Probability Basics. */

export const PS_PROB_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Sample space of two dice",
    setup: "Two fair dice are rolled. Find $P(\\text{sum}=7)$ and $P(\\text{sum}\\ge10)$.",
    steps: [
      { text: "The sample space is all ordered pairs $(a,b)$ with $a,b\\in\\{1,\\dots,6\\}$.", why: "Order matters when the dice are distinguishable." },
      { text: "$|\\Omega|=6\\times6=36$ equally likely outcomes.", why: "Multiplication principle." },
      { text: "Sum $7$ occurs for $(1,6),(2,5),(3,4),(4,3),(5,2),(6,1)$.", why: "List systematically to avoid omissions." },
      { text: "That is $6$ favourable outcomes, so $P=\\tfrac{6}{36}=\\tfrac16$.", why: "Equally likely model." },
      { text: "Sum $10$: $(4,6),(5,5),(6,4)$ — $3$ outcomes.", why: "Continue the enumeration." },
      { text: "Sum $11$: $(5,6),(6,5)$ — $2$ outcomes; sum $12$: $(6,6)$ — $1$ outcome.", why: "Cover the whole event." },
      { text: "Total favourable $=3+2+1=6$, so $P(\\text{sum}\\ge10)=\\tfrac{6}{36}=\\tfrac16$.", why: "Add disjoint cases." },
      { text: "Both probabilities lie in $[0,1]$ and the full distribution of sums adds to $1$.", why: "Axiom check." },
    ],
    result: "$P(\\text{sum}=7)=\\tfrac16$ and $P(\\text{sum}\\ge10)=\\tfrac16$",
    check: "Sum $7$ is the single most likely total, matching the peak of the triangular distribution.",
    mistake: "Counting $\\{3,4\\}$ and $\\{4,3\\}$ as one outcome, which shrinks the count to $21$ and breaks equal likelihood.",
    life: "Casinos and insurance both price risk by counting outcomes exactly like this; a miscount of the sample space is a miscount of the payout.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Complement and addition rules",
    setup: "$P(A)=0.5$, $P(B)=0.4$, $P(A\\cap B)=0.2$. Find $P(A\\cup B)$, $P(A^{c})$, and $P(A\\cap B^{c})$.",
    steps: [
      { text: "Addition rule: $P(A\\cup B)=P(A)+P(B)-P(A\\cap B)$.", why: "Subtract the double-counted overlap." },
      { text: "$=0.5+0.4-0.2=0.7$.", why: "Substitute." },
      { text: "Complement rule: $P(A^{c})=1-P(A)=0.5$.", why: "Total probability is $1$." },
      { text: "Decompose $A$ into disjoint pieces: $A=(A\\cap B)\\cup(A\\cap B^{c})$.", why: "Every point of $A$ is either in $B$ or not." },
      { text: "So $P(A\\cap B^{c})=P(A)-P(A\\cap B)=0.5-0.2=0.3$.", why: "Additivity on disjoint sets." },
      { text: "Similarly $P(B\\cap A^{c})=0.4-0.2=0.2$.", why: "Same logic for $B$." },
      { text: "Neither event: $P((A\\cup B)^{c})=1-0.7=0.3$.", why: "Complement of the union." },
      { text: "Check the four disjoint cells: $0.2+0.3+0.2+0.3=1$.", why: "The partition must sum to $1$." },
    ],
    result: "$P(A\\cup B)=0.7$, $P(A^{c})=0.5$, $P(A\\cap B^{c})=0.3$",
    check: "Independence would need $P(A\\cap B)=0.2=0.5(0.4)$ — it holds here, a coincidence worth noting.",
    mistake: "Using $P(A\\cup B)=P(A)+P(B)$ when the events overlap.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Counting with combinations",
    setup: "From $8$ men and $6$ women, a committee of $5$ is chosen at random. Find $P(\\text{exactly }3\\text{ women})$.",
    steps: [
      { text: "Total committees: $\\binom{14}{5}$.", why: "Order does not matter." },
      { text: "$\\binom{14}{5}=\\dfrac{14\\cdot13\\cdot12\\cdot11\\cdot10}{120}=2002$.", why: "Cancel factorials carefully." },
      { text: "Choose $3$ women from $6$: $\\binom{6}{3}=20$.", why: "Split the selection by group." },
      { text: "Choose the remaining $2$ men from $8$: $\\binom{8}{2}=28$.", why: "The committee size fixes the second count." },
      { text: "Favourable committees $=20\\times28=560$.", why: "Multiplication principle across independent choices." },
      { text: "$P=\\dfrac{560}{2002}$.", why: "Equally likely committees." },
      { text: "Simplify by $14$: $\\dfrac{40}{143}\\approx0.2797$.", why: "Reduce the fraction." },
      { text: "So about $28\\%$ of committees have exactly three women.", why: "Interpretation." },
    ],
    result: "$P=\\tfrac{40}{143}\\approx0.280$",
    check: "Summing the hypergeometric probabilities for $0$ to $5$ women gives $1$.",
    mistake: "Using permutations $P(14,5)$, which counts the same committee in $120$ different orders.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "At least one — via the complement",
    setup: "A machine has $4$ independent components each failing with probability $0.1$. Find $P(\\text{at least one fails})$.",
    steps: [
      { text: "\"At least one\" is the complement of \"none\".", why: "Complements turn a messy union into a single product." },
      { text: "$P(\\text{a given component works})=1-0.1=0.9$.", why: "Complement per component." },
      { text: "Independence gives $P(\\text{all work})=0.9^{4}$.", why: "Multiply for independent events." },
      { text: "$0.9^{2}=0.81$ and $0.9^{4}=0.81^{2}=0.6561$.", why: "Square twice." },
      { text: "So $P(\\text{at least one fails})=1-0.6561=0.3439$.", why: "Complement rule." },
      { text: "Compare the naive sum $4(0.1)=0.4$, which overcounts multiple failures.", why: "Shows why inclusion–exclusion matters." },
      { text: "Exact inclusion–exclusion: $0.4-\\binom42(0.01)+\\binom43(0.001)-0.0001=0.4-0.06+0.004-0.0001=0.3439$.", why: "Confirms the complement result." },
      { text: "Both routes agree, so the answer is $0.3439$.", why: "Cross-validated." },
    ],
    result: "$P(\\text{at least one fails})=1-0.9^{4}=0.3439$",
    check: "The answer is below the naive $0.4$, as it must be.",
    mistake: "Adding individual failure probabilities and reporting $0.4$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Birthday-style collision problem",
    setup: "Five people each pick a random day of the week. Find the probability that at least two pick the same day.",
    steps: [
      { text: "Total outcomes: $7^{5}=16807$.", why: "Each person has $7$ independent choices." },
      { text: "Complement: all five pick different days.", why: "Distinctness is easier to count." },
      { text: "Ordered choices without repetition: $7\\cdot6\\cdot5\\cdot4\\cdot3$.", why: "Each pick removes one option." },
      { text: "$=2520$.", why: "Multiply step by step." },
      { text: "$P(\\text{all different})=\\dfrac{2520}{16807}\\approx0.1500$.", why: "Ratio of counts." },
      { text: "So $P(\\text{at least one match})\\approx1-0.150=0.850$.", why: "Complement rule." },
      { text: "With only $8$ people a match is essentially certain, since $7$ days cannot host $8$ distinct picks.", why: "Pigeonhole principle." },
      { text: "Collisions are far more likely than intuition suggests — the same effect as the classic birthday problem.", why: "Interpretation." },
    ],
    result: "$P(\\text{at least two share a day})\\approx0.850$",
    check: "$2520/16807=P(7,5)/7^{5}$, and $P(7,5)<7^{5}$ as required.",
    mistake: "Computing $\\binom{5}{2}/7\\approx1.43$, which is not even a valid probability.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Inclusion–exclusion with three events",
    setup: "In a class, $60\\%$ study maths, $50\\%$ physics, $40\\%$ chemistry; $30\\%$ maths and physics, $20\\%$ maths and chemistry, $15\\%$ physics and chemistry, $10\\%$ all three. Find $P(\\text{at least one})$ and $P(\\text{exactly one})$.",
    steps: [
      { text: "Inclusion–exclusion: $P(M\\cup P\\cup C)=\\sum P-\\sum P(\\text{pairs})+P(\\text{triple})$.", why: "Alternating corrections." },
      { text: "Singles: $0.6+0.5+0.4=1.5$.", why: "First layer." },
      { text: "Pairs: $0.3+0.2+0.15=0.65$.", why: "Second layer, subtracted." },
      { text: "$P(M\\cup P\\cup C)=1.5-0.65+0.10=0.95$.", why: "Combine all three layers." },
      { text: "So $5\\%$ of the class studies none of the three.", why: "Complement." },
      { text: "Exactly one for maths: $0.6-0.3-0.2+0.1=0.2$.", why: "Remove pair overlaps, add back the triple counted twice." },
      { text: "Physics: $0.5-0.3-0.15+0.1=0.15$; chemistry: $0.4-0.2-0.15+0.1=0.15$.", why: "Same formula per subject." },
      { text: "$P(\\text{exactly one})=0.2+0.15+0.15=0.5$.", why: "Add the three disjoint pieces." },
    ],
    result: "$P(\\text{at least one})=0.95$ and $P(\\text{exactly one})=0.50$",
    check: "Exactly two $=0.65-3(0.1)=0.35$; then $0.5+0.35+0.1=0.95$. Consistent.",
    mistake: "Subtracting the triple intersection instead of adding it back.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Cards: probability of a full house",
    setup: "Five cards are dealt from a standard $52$-card deck. Find $P(\\text{full house})$.",
    steps: [
      { text: "Total hands: $\\binom{52}{5}=2{,}598{,}960$.", why: "Unordered deals." },
      { text: "A full house is three of one rank and two of another.", why: "Definition drives the count." },
      { text: "Choose the triple's rank: $13$ ways.", why: "First structural choice." },
      { text: "Choose $3$ of its $4$ suits: $\\binom43=4$.", why: "Suits within the rank." },
      { text: "Choose the pair's rank from the remaining $12$: $12$ ways.", why: "Ranks must differ." },
      { text: "Choose $2$ of its $4$ suits: $\\binom42=6$.", why: "Suits for the pair." },
      { text: "Favourable hands $=13\\cdot4\\cdot12\\cdot6=3744$.", why: "Multiplication principle." },
      { text: "$P=\\dfrac{3744}{2598960}\\approx0.00144$, about $1$ in $694$.", why: "Divide." },
    ],
    result: "$P(\\text{full house})=\\tfrac{3744}{2598960}\\approx0.144\\%$",
    check: "This sits between four-of-a-kind ($0.024\\%$) and a flush ($0.20\\%$), matching poker rankings.",
    mistake: "Using $13\\cdot12$ ordered rank pairs but then also dividing by $2$, or using $\\binom{13}{2}$, which loses the triple/pair distinction.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Geometric probability on a stick",
    setup: "A stick of length $1$ is broken at a uniformly random point $X$. Find $P(\\text{longer piece}>2\\times\\text{shorter piece})$.",
    steps: [
      { text: "$X\\sim\\mathrm{Uniform}(0,1)$, so probabilities are lengths of subsets of $[0,1]$.", why: "Geometric probability model." },
      { text: "Pieces have lengths $X$ and $1-X$.", why: "Set up the comparison." },
      { text: "Case 1: $X$ is the shorter piece, i.e. $X<0.5$, and we need $1-X>2X$.", why: "Split by which piece is shorter." },
      { text: "$1-X>2X\\Rightarrow X<\\tfrac13$.", why: "Solve the inequality." },
      { text: "Case 2: $X>0.5$ and $X>2(1-X)$, giving $3X>2$, so $X>\\tfrac23$.", why: "Symmetric case." },
      { text: "Favourable set: $[0,\\tfrac13)\\cup(\\tfrac23,1]$.", why: "Union of two intervals." },
      { text: "Total length $=\\tfrac13+\\tfrac13=\\tfrac23$.", why: "Uniform density means length equals probability." },
      { text: "So $P=\\tfrac23\\approx0.667$.", why: "Answer." },
    ],
    result: "$P=\\tfrac23$",
    check: "The complementary event ($\\text{ratio}\\le2$) is $X\\in[\\tfrac13,\\tfrac23]$, of length $\\tfrac13$.",
    mistake: "Forgetting the symmetric second case and reporting $\\tfrac13$.",
  },
];

export const PS_PROB_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Conditional probability from a table",
    setup: "Of $200$ students, $120$ passed maths, $90$ passed physics, and $70$ passed both. Find $P(\\text{physics}\\mid\\text{maths})$.",
    steps: [
      { text: "Convert counts to probabilities: $P(M)=120/200=0.6$.", why: "Relative frequencies." },
      { text: "$P(P)=90/200=0.45$ and $P(M\\cap P)=70/200=0.35$.", why: "Same for the others." },
      { text: "Definition: $P(P\\mid M)=\\dfrac{P(M\\cap P)}{P(M)}$.", why: "Conditioning restricts the sample space to $M$." },
      { text: "$=\\dfrac{0.35}{0.6}=\\dfrac{7}{12}\\approx0.583$.", why: "Divide." },
      { text: "Equivalently $70/120$ directly from the counts.", why: "Same ratio without the conversion." },
      { text: "Compare with the unconditional $P(P)=0.45$.", why: "Conditioning changed the answer." },
      { text: "Since $0.583>0.45$, passing maths is positive evidence for passing physics.", why: "Interpretation." },
      { text: "So the events are dependent: $P(M\\cap P)=0.35\\ne0.6(0.45)=0.27$.", why: "Independence test." },
    ],
    result: "$P(\\text{physics}\\mid\\text{maths})=\\tfrac{7}{12}\\approx0.583$",
    check: "$P(M\\mid P)=70/90\\approx0.778$, and both conditionals exceed their unconditional counterparts.",
    mistake: "Dividing by the wrong marginal, i.e. computing $70/90$ when maths was the given condition.",
    life: "Hospital triage runs on conditional probability: the chance of a condition given a positive screen is what decides the next test, not the raw prevalence.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Multiplication rule without replacement",
    setup: "An urn has $5$ red and $3$ blue balls. Two are drawn without replacement. Find $P(\\text{both red})$ and $P(\\text{second red})$.",
    steps: [
      { text: "$P(\\text{first red})=\\tfrac58$.", why: "Five of eight balls are red." },
      { text: "Given a red first draw, $4$ reds remain out of $7$ balls.", why: "The sample space shrinks." },
      { text: "$P(\\text{second red}\\mid\\text{first red})=\\tfrac47$.", why: "Conditional probability." },
      { text: "Multiplication rule: $P(\\text{both red})=\\tfrac58\\cdot\\tfrac47=\\tfrac{20}{56}=\\tfrac{5}{14}$.", why: "Chain the two stages." },
      { text: "For the second draw alone, split by the first colour.", why: "Law of total probability." },
      { text: "$P(\\text{2nd red})=\\tfrac58\\cdot\\tfrac47+\\tfrac38\\cdot\\tfrac57$.", why: "Two disjoint routes." },
      { text: "$=\\tfrac{20}{56}+\\tfrac{15}{56}=\\tfrac{35}{56}=\\tfrac58$.", why: "Add." },
      { text: "The second draw has the same marginal probability as the first — the symmetry of exchangeability.", why: "Elegant structural fact." },
    ],
    result: "$P(\\text{both red})=\\tfrac{5}{14}$ and $P(\\text{second red})=\\tfrac58$",
    check: "$\\binom52/\\binom82=10/28=5/14$ confirms the first answer.",
    mistake: "Reusing $\\tfrac58$ for the second draw inside the joint probability, which assumes replacement.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Law of total probability across three factories",
    setup: "Factories A, B, C supply $50\\%$, $30\\%$, $20\\%$ of parts with defect rates $1\\%$, $2\\%$, $5\\%$. Find the overall defect rate.",
    steps: [
      { text: "The factories partition the supply: $P(A)=0.5$, $P(B)=0.3$, $P(C)=0.2$.", why: "They are exhaustive and disjoint." },
      { text: "Conditional defect rates: $P(D\\mid A)=0.01$, $P(D\\mid B)=0.02$, $P(D\\mid C)=0.05$.", why: "Given information." },
      { text: "Total probability: $P(D)=\\sum P(D\\mid F_i)P(F_i)$.", why: "Weight each branch by its share." },
      { text: "A contributes $0.01(0.5)=0.005$.", why: "Branch by branch." },
      { text: "B contributes $0.02(0.3)=0.006$.", why: "Second branch." },
      { text: "C contributes $0.05(0.2)=0.010$.", why: "Third branch." },
      { text: "$P(D)=0.005+0.006+0.010=0.021$.", why: "Add the branches." },
      { text: "So $2.1\\%$ of all parts are defective, and C alone accounts for nearly half of them.", why: "Interpretation for quality control." },
    ],
    result: "$P(\\text{defective})=0.021$, i.e. $2.1\\%$",
    check: "The answer lies between the smallest ($1\\%$) and largest ($5\\%$) rates, as any weighted average must.",
    mistake: "Averaging $1\\%,2\\%,5\\%$ to get $2.67\\%$, ignoring the different supply shares.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Bayes' theorem for a medical test",
    setup: "A disease affects $1\\%$ of people. A test has sensitivity $95\\%$ and specificity $90\\%$. Find $P(\\text{disease}\\mid\\text{positive})$.",
    steps: [
      { text: "Prior: $P(D)=0.01$, so $P(D^{c})=0.99$.", why: "Base rate." },
      { text: "Sensitivity: $P(+\\mid D)=0.95$.", why: "True-positive rate." },
      { text: "Specificity $0.90$ means $P(+\\mid D^{c})=0.10$.", why: "False-positive rate is one minus specificity." },
      { text: "Numerator: $P(+\\mid D)P(D)=0.95(0.01)=0.0095$.", why: "Joint probability of disease and a positive test." },
      { text: "False positives: $0.10(0.99)=0.099$.", why: "Much larger because healthy people are far more numerous." },
      { text: "$P(+)=0.0095+0.099=0.1085$.", why: "Law of total probability." },
      { text: "$P(D\\mid+)=\\dfrac{0.0095}{0.1085}\\approx0.0876$.", why: "Bayes' theorem." },
      { text: "So only about $8.8\\%$ of positives are true cases, despite a $95\\%$ sensitive test.", why: "The base-rate effect." },
    ],
    result: "$P(\\text{disease}\\mid\\text{positive})\\approx0.088$",
    check: "In $10{,}000$ people: $95$ true positives against $990$ false positives, and $95/1085\\approx0.088$.",
    mistake: "Reporting $95\\%$ — confusing $P(+\\mid D)$ with $P(D\\mid+)$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Independence: verify, do not assume",
    setup: "One card is drawn from a deck. Let $A=\\{\\text{king}\\}$, $B=\\{\\text{heart}\\}$, $C=\\{\\text{face card}\\}$. Test $A$ vs $B$ and $A$ vs $C$.",
    steps: [
      { text: "$P(A)=4/52=1/13$ and $P(B)=13/52=1/4$.", why: "Count favourable cards." },
      { text: "$P(A\\cap B)=1/52$ — only the king of hearts.", why: "Single card in both events." },
      { text: "Test: $P(A)P(B)=\\tfrac{1}{13}\\cdot\\tfrac14=\\tfrac{1}{52}$.", why: "Independence criterion." },
      { text: "Since $P(A\\cap B)=P(A)P(B)$, suit and rank are independent.", why: "Structure of the deck." },
      { text: "Now $P(C)=12/52=3/13$ (jacks, queens, kings).", why: "Face cards." },
      { text: "$P(A\\cap C)=P(A)=1/13$ because every king is a face card.", why: "$A\\subset C$." },
      { text: "But $P(A)P(C)=\\tfrac{1}{13}\\cdot\\tfrac{3}{13}=\\tfrac{3}{169}\\ne\\tfrac{1}{13}$.", why: "Products disagree." },
      { text: "So $A$ and $C$ are dependent — nested events are never independent unless one has probability $0$ or $1$.", why: "General principle." },
    ],
    result: "$A,B$ independent; $A,C$ dependent",
    check: "$P(A\\mid B)=1/13=P(A)$, while $P(A\\mid C)=4/12=1/3\\ne1/13$.",
    mistake: "Treating \"different-sounding\" events as automatically independent.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Monty Hall by conditioning",
    setup: "Three doors, one prize. You pick door 1; the host (who knows) opens door 3 to reveal no prize. Should you switch?",
    steps: [
      { text: "Prior: the prize is behind each door with probability $\\tfrac13$.", why: "Symmetric setup." },
      { text: "If the prize is behind door 1, the host may open door 2 or 3, each with probability $\\tfrac12$.", why: "Host is indifferent." },
      { text: "If the prize is behind door 2, the host must open door 3.", why: "He cannot reveal the prize or your door." },
      { text: "If the prize is behind door 3, the host cannot open door 3.", why: "Probability $0$ for this branch." },
      { text: "$P(\\text{host opens }3\\mid\\text{prize }1)=\\tfrac12$, so joint $=\\tfrac13\\cdot\\tfrac12=\\tfrac16$.", why: "Multiply." },
      { text: "$P(\\text{host opens }3\\mid\\text{prize }2)=1$, so joint $=\\tfrac13$.", why: "Multiply." },
      { text: "$P(\\text{host opens }3)=\\tfrac16+\\tfrac13=\\tfrac12$.", why: "Total probability." },
      { text: "Bayes: $P(\\text{prize }1\\mid\\cdot)=\\dfrac{1/6}{1/2}=\\tfrac13$ and $P(\\text{prize }2\\mid\\cdot)=\\dfrac{1/3}{1/2}=\\tfrac23$.", why: "Switching doubles your chance." },
    ],
    result: "Switch: $\\tfrac23$ versus $\\tfrac13$ for staying",
    check: "Over $300$ plays you would win about $200$ by switching and $100$ by staying.",
    mistake: "Arguing \"two doors left, so it is $50$–$50$\"; the host's constrained choice carries information.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Sequential Bayes updating",
    setup: "A coin is fair with probability $0.5$ or two-headed with probability $0.5$. It lands heads twice. Find the posterior that it is two-headed.",
    steps: [
      { text: "Priors: $P(F)=P(T)=0.5$.", why: "Two hypotheses." },
      { text: "Likelihood for the fair coin: $P(HH\\mid F)=0.5^{2}=0.25$.", why: "Independent flips." },
      { text: "Likelihood for the two-headed coin: $P(HH\\mid T)=1$.", why: "Heads is certain." },
      { text: "Joint for fair: $0.25(0.5)=0.125$.", why: "Prior times likelihood." },
      { text: "Joint for two-headed: $1(0.5)=0.5$.", why: "Same product." },
      { text: "Evidence: $P(HH)=0.125+0.5=0.625$.", why: "Sum over hypotheses." },
      { text: "Posterior: $P(T\\mid HH)=\\dfrac{0.5}{0.625}=0.8$.", why: "Bayes' theorem." },
      { text: "One more head updates to $\\dfrac{0.5}{0.5+0.0625}=0.889$; evidence accumulates multiplicatively.", why: "Sequential updating." },
    ],
    result: "$P(\\text{two-headed}\\mid HH)=0.8$",
    check: "Updating one flip at a time gives $\\tfrac23$ then $0.8$ — the same as batching both flips.",
    mistake: "Ignoring the prior and quoting the likelihood ratio as the posterior.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Reliability of series and parallel systems",
    setup: "Three independent components with reliabilities $0.9$, $0.8$, $0.95$. Compare a series arrangement with a parallel one, and find the reliability of one in series with a parallel pair.",
    steps: [
      { text: "Series: the system works only if all components work.", why: "Weakest-link structure." },
      { text: "$R_{\\text{series}}=0.9(0.8)(0.95)=0.684$.", why: "Multiply reliabilities." },
      { text: "Parallel: the system fails only if all fail.", why: "Redundancy." },
      { text: "Failure probabilities: $0.1$, $0.2$, $0.05$; product $=0.001$.", why: "Independent failures." },
      { text: "$R_{\\text{parallel}}=1-0.001=0.999$.", why: "Complement." },
      { text: "Mixed design: component $1$ in series with a parallel block of $2$ and $3$.", why: "Realistic layout." },
      { text: "Parallel block: $1-0.2(0.05)=1-0.01=0.99$.", why: "Block reliability first." },
      { text: "Whole system: $0.9(0.99)=0.891$.", why: "Then multiply along the series path." },
    ],
    result: "$R_{\\text{series}}=0.684$, $R_{\\text{parallel}}=0.999$, mixed design $=0.891$",
    check: "Every configuration's reliability lies between the worst component ($0.8$) bound for series and $1$ for full redundancy.",
    mistake: "Adding reliabilities in parallel, which can exceed $1$ and is meaningless.",
  },
];
